import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";

import { cx } from "../../app/format";
import { RetryableLazyRoute } from "../../components/LazyRouteBoundary";
import { TechnicalDetails } from "../../components/TechnicalDetails";
import { MarkdownMessage } from "./MarkdownMessage";
import {
  AssistantNotesTab as TestAssistantNotesTab,
  type AssistantNotesTabProps
} from "./AssistantNotesTab";
import type {
  AssistantCitation,
  AssistantDockProps,
  AssistantDockTab,
  AssistantGroundingState,
  AssistantMessage,
  AssistantProposedAction,
  AssistantTechnicalDetails,
  AssistantTurnEvent,
  ProjectNote,
  ProjectNoteInput,
  ProjectNoteKind,
  ProjectNoteSubjectRef,
  AssistantTurnState
} from "./types";

const assistantDockCompactQuery = "(max-width: 1180px)";
const ASSISTANT_DOCK_ROOT_ATTR = "data-tcs-assistant-dock";
const ASSISTANT_DOCK_MODAL_ATTR = "data-tcs-assistant-modal";

const ASSISTANT_FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])"
].join(",");

const loadAssistantTurnTrace = async (attempt: number) => {
  const module =
    import.meta.env.DEV && attempt > 0
      ? ((await import(
          /* @vite-ignore */ `./AssistantTurnTrace.tsx?tcsLazyRetry=${attempt.toString()}`
        )) as typeof import("./AssistantTurnTrace"))
      : await import("./AssistantTurnTrace");

  return { default: module.AssistantTurnTrace };
};

const loadAssistantNotesTab = async (attempt: number) => {
  const module =
    import.meta.env.DEV && attempt > 0
      ? ((await import(
          /* @vite-ignore */ `./AssistantNotesTab.tsx?tcsLazyRetry=${attempt.toString()}`
        )) as typeof import("./AssistantNotesTab"))
      : await import("./AssistantNotesTab");

  return { default: module.AssistantNotesTab };
};

type AssistantDockViewportHost = typeof globalThis & {
  matchMedia?: (query: string) => MediaQueryList;
};

const assistantDockMediaQuery = (): MediaQueryList | null => {
  const matchMedia = (globalThis as AssistantDockViewportHost).matchMedia;

  return typeof matchMedia === "function"
    ? matchMedia(assistantDockCompactQuery)
    : null;
};

const isCompactAssistantDockViewport = (): boolean =>
  assistantDockMediaQuery()?.matches ?? false;

function assistantFocusableElements(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(ASSISTANT_FOCUSABLE_SELECTOR))
    .filter((element) => element.getClientRects().length > 0)
    .filter((element) => element.getAttribute("aria-hidden") !== "true");
}

const DEFAULT_PROMPTS: readonly string[] = [
  "What is missing?",
  "What needs a client decision?",
  "What should I check before specifying?"
];
const MAX_RETAINED_DRAFT_CONTEXTS = 25;

interface AssistantDraftState {
  askDraft: string;
  noteDraft: string;
  noteDraftKind: ProjectNoteKind;
  noteDraftSubjectId: string;
}

const EMPTY_DRAFT_STATE: AssistantDraftState = {
  askDraft: "",
  noteDraft: "",
  noteDraftKind: "clarification",
  noteDraftSubjectId: ""
};

// The host page can unmount AssistantDock entirely mid-session (e.g. App.tsx
// renders it as null while the project surface is loading / falls back to
// the setup wizard). Component-local useState does not survive that — so
// drafts are mirrored to sessionStorage, keyed by the same activeDraftContext
// used in memory, and rehydrated on every mount.
const ASSISTANT_DRAFT_STORAGE_KEY = "tcs:assistant-drafts:v1";
const ASSISTANT_NOTE_DRAFT_KINDS: readonly ProjectNoteKind[] = [
  "clarification",
  "exclusion",
  "commissioning",
  "requirement-amendment"
];

type AssistantDockStorageHost = typeof globalThis & {
  sessionStorage?: Storage;
};

function assistantDockSessionStorage(): Storage | null {
  try {
    return (globalThis as AssistantDockStorageHost).sessionStorage ?? null;
  } catch {
    return null;
  }
}

function isAssistantDraftState(value: unknown): value is AssistantDraftState {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as Partial<AssistantDraftState>;

  return (
    typeof candidate.askDraft === "string" &&
    typeof candidate.noteDraft === "string" &&
    typeof candidate.noteDraftSubjectId === "string" &&
    typeof candidate.noteDraftKind === "string" &&
    ASSISTANT_NOTE_DRAFT_KINDS.includes(candidate.noteDraftKind as ProjectNoteKind)
  );
}

function readPersistedAssistantDrafts(): Map<string, AssistantDraftState> {
  const storage = assistantDockSessionStorage();

  if (!storage) {
    return new Map();
  }

  try {
    const raw = storage.getItem(ASSISTANT_DRAFT_STORAGE_KEY);

    if (!raw) {
      return new Map();
    }

    const parsed: unknown = JSON.parse(raw);

    if (!Array.isArray(parsed)) {
      return new Map();
    }

    const entries: Array<[string, AssistantDraftState]> = [];

    for (const entry of parsed) {
      if (
        Array.isArray(entry) &&
        entry.length === 2 &&
        typeof entry[0] === "string" &&
        isAssistantDraftState(entry[1])
      ) {
        entries.push([entry[0], entry[1]]);
      }
    }

    return new Map(entries);
  } catch {
    return new Map();
  }
}

function writePersistedAssistantDrafts(drafts: Map<string, AssistantDraftState>): void {
  const storage = assistantDockSessionStorage();

  if (!storage) {
    return;
  }

  try {
    storage.setItem(
      ASSISTANT_DRAFT_STORAGE_KEY,
      JSON.stringify(Array.from(drafts.entries()))
    );
  } catch {
    // Best-effort persistence only — storage may be full, disabled, or unavailable.
  }
}

const CHANGE_INSTRUCTION_PATTERN =
  /^\s*(add|amend|apply|change|delete|exclude|insert|make|mark|move|remove|replace|set|switch|update)\b/i;

function isLikelyProjectChangeInstruction(text: string): boolean {
  const trimmed = text.trim();

  return (
    trimmed.length > 0 &&
    !trimmed.endsWith("?") &&
    CHANGE_INSTRUCTION_PATTERN.test(trimmed)
  );
}

const runCallback = (result: void | Promise<void>) => {
  void Promise.resolve(result).catch(() => undefined);
};

function buildDraftAction({
  id,
  instructionText
}: {
  id: string;
  instructionText: string;
}): AssistantProposedAction {
  return {
    id,
    title: "Review proposed change",
    description:
      "This looks like a project change — held for review before applying.",
    instructionText,
    kind: "project-change",
    status: "draft",
    createdAt: new Date().toISOString()
  };
}

function buildChatCreatedNoteInput(input: {
  text: string;
  messages: readonly AssistantMessage[];
  projectLabel: string;
  subjectOptions: readonly ProjectNoteSubjectRef[] | undefined;
}): ProjectNoteInput | null {
  const trimmed = input.text.trim();
  const match =
    /^(?:please\s+)?(?:make|take|add|capture|create|save)\s+(?:me\s+)?(?:a\s+)?note(?:\s+(?:of|about|on|that))?(?:\s*[:\-]\s*|\s+)?(.*)$/i.exec(
      trimmed
    );

  if (!match) {
    return null;
  }

  const captured = (match[1]?.trim() ?? "")
    .replace(/^(?:this|that)\s*[:\-]\s*/i, "")
    .trim();
  const previousContext = [...input.messages]
    .reverse()
    .find((message) => message.role === "assistant" || message.role === "user");
  const text =
    captured && !/^(this|that)$/i.test(captured)
      ? captured
      : previousContext?.text.trim() || "Capture this assistant conversation context.";

  const subjectRef = input.subjectOptions?.[0];

  return {
    kind: inferNoteKind(text),
    text,
    ...(subjectRef ? { subjectRef } : {}),
    authorType: "ai",
    aiProvenance: {
      why: "The user asked the assistant to capture this as a project note.",
      where: `Assistant chat / ${input.projectLabel}`,
      toolName: "notes.applyTypedNote",
      ...(previousContext?.id ? { sourceMessageId: previousContext.id } : {})
    }
  };
}

function inferNoteKind(text: string): ProjectNoteKind {
  const normalised = text.toLowerCase();

  if (
    /\b(exclude|exclusion|omit|not included|by others|by-others)\b/.test(normalised)
  ) {
    return "exclusion";
  }

  if (/\b(commission|commissioning|witness|handover|test|testing)\b/.test(normalised)) {
    return "commissioning";
  }

  if (
    /\b(require|requirement|must|shall|provide|allow for|include)\b/.test(normalised)
  ) {
    return "requirement-amendment";
  }

  return "clarification";
}

export function AssistantDock({
  open,
  onToggle,
  tab,
  onTab,
  copilot,
  notes,
  projectContextId,
  projectLabel = "this project",
  collapsible = true,
  className
}: AssistantDockProps) {
  const [compact, setCompact] = useState(() => isCompactAssistantDockViewport());
  const [draftsByContext, setDraftsByContext] = useState<
    Map<string, AssistantDraftState>
  >(() => readPersistedAssistantDrafts());
  const rootRef = useRef<HTMLElement | null>(null);
  const launcherRef = useRef<HTMLButtonElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const restoreFocusOnCloseRef = useRef(false);
  const actionSequence = useRef(0);
  const modal = open && compact;
  const activeDraftContext =
    projectContextId?.trim() || projectLabel.trim() || "assistant-draft";
  const activeDraft = draftsByContext.get(activeDraftContext) ?? EMPTY_DRAFT_STATE;
  const updateActiveDraft = useCallback(
    (patch: Partial<AssistantDraftState>) => {
      setDraftsByContext((current) => {
        const next = new Map(current);
        const currentDraft = next.get(activeDraftContext) ?? EMPTY_DRAFT_STATE;

        next.delete(activeDraftContext);
        next.set(activeDraftContext, { ...currentDraft, ...patch });

        if (next.size > MAX_RETAINED_DRAFT_CONTEXTS) {
          for (const candidate of next.keys()) {
            if (next.size <= MAX_RETAINED_DRAFT_CONTEXTS) {
              break;
            }

            if (candidate !== activeDraftContext) {
              next.delete(candidate);
            }
          }
        }

        return next;
      });
    },
    [activeDraftContext]
  );

  useEffect(() => {
    writePersistedAssistantDrafts(draftsByContext);
  }, [draftsByContext]);

  useEffect(() => {
    const media = assistantDockMediaQuery();

    if (!media) {
      return;
    }

    const syncMode = () => setCompact(media.matches);

    syncMode();
    media.addEventListener("change", syncMode);

    return () => media.removeEventListener("change", syncMode);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    const root = document.documentElement;

    if (open && compact) {
      root.setAttribute(ASSISTANT_DOCK_ROOT_ATTR, "compact-open");
    } else {
      root.removeAttribute(ASSISTANT_DOCK_ROOT_ATTR);
    }

    root.toggleAttribute(ASSISTANT_DOCK_MODAL_ATTR, modal);

    return () => {
      root.removeAttribute(ASSISTANT_DOCK_ROOT_ATTR);
      root.removeAttribute(ASSISTANT_DOCK_MODAL_ATTR);
    };
  }, [compact, modal, open]);

  useEffect(() => {
    if (!modal || typeof document === "undefined") {
      return undefined;
    }

    const previousBodyOverflow = document.body.style.overflow;
    const previousDocumentOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousDocumentOverflow;
    };
  }, [modal]);

  useEffect(() => {
    if (!modal || typeof document === "undefined" || typeof window === "undefined") {
      return undefined;
    }

    const activeElement =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    if (!restoreFocusOnCloseRef.current && !rootRef.current?.contains(activeElement)) {
      previousFocusRef.current = activeElement;
    }

    restoreFocusOnCloseRef.current = true;

    const focusFrame = window.requestAnimationFrame(() => {
      const root = rootRef.current;
      const target =
        closeButtonRef.current ??
        (root ? assistantFocusableElements(root)[0] : undefined) ??
        root;

      target?.focus({ preventScroll: true });
    });
    const containFocus = (event: FocusEvent) => {
      const root = rootRef.current;
      const target = event.target;

      if (!root || !(target instanceof Node) || root.contains(target)) {
        return;
      }

      const nextFocus =
        closeButtonRef.current ?? assistantFocusableElements(root)[0] ?? root;

      window.requestAnimationFrame(() => {
        nextFocus.focus({ preventScroll: true });
      });
    };

    document.addEventListener("focusin", containFocus, true);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("focusin", containFocus, true);
    };
  }, [modal]);

  useEffect(() => {
    if (
      open ||
      !restoreFocusOnCloseRef.current ||
      typeof document === "undefined" ||
      typeof window === "undefined"
    ) {
      return undefined;
    }

    const restoreFrame = window.requestAnimationFrame(() => {
      const previousFocus = previousFocusRef.current;
      const fallback = launcherRef.current;
      const canRestorePrevious =
        previousFocus?.isConnected === true &&
        previousFocus !== document.body &&
        previousFocus !== document.documentElement;

      if (canRestorePrevious) {
        previousFocus.focus({ preventScroll: true });
      } else {
        fallback?.focus({ preventScroll: true });
      }

      previousFocusRef.current = null;
      restoreFocusOnCloseRef.current = false;
    });

    return () => window.cancelAnimationFrame(restoreFrame);
  }, [open]);

  const nextActionId = (): string => {
    if (copilot.createProposedActionId) {
      return copilot.createProposedActionId();
    }

    actionSequence.current += 1;
    return `assistant-action-${actionSequence.current}`;
  };

  const rememberFocusBeforeOpen = () => {
    if (
      typeof document !== "undefined" &&
      document.activeElement instanceof HTMLElement
    ) {
      previousFocusRef.current = document.activeElement;
      restoreFocusOnCloseRef.current = true;
    }

    onToggle(true);
  };

  const closeAssistant = () => onToggle(false);

  const handleAssistantKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (!modal) {
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      closeAssistant();
      return;
    }

    if (event.key !== "Tab") {
      return;
    }

    const root = rootRef.current;

    if (!root) {
      return;
    }

    const focusable = assistantFocusableElements(root);

    if (focusable.length === 0) {
      event.preventDefault();
      root.focus({ preventScroll: true });
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (!root.contains(active)) {
      event.preventDefault();
      first?.focus({ preventScroll: true });
      return;
    }

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last?.focus({ preventScroll: true });
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first?.focus({ preventScroll: true });
    }
  };

  if (!open) {
    return (
      <button
        ref={launcherRef}
        type="button"
        data-tcs-assistant-launcher="true"
        onClick={rememberFocusBeforeOpen}
        aria-label={`Assistant ${notes.notes.length}`}
        aria-expanded="false"
        title="Open assistant"
        className={cx(
          // SE-05: `tcsAssistantLauncher` (see index.css) reserves the host page's
          // real sticky-bottom-bar height so this floating launcher never overlaps
          // the sticky Continue bar. `pointer-events-auto` guards against inheriting
          // `pointer-events: none` from a reserved-space ancestor wrapper.
          "tcsAssistantLauncher pointer-events-auto fixed right-4 z-[45] flex cursor-pointer items-center gap-2 rounded-[11px] border border-[var(--line)] bg-[var(--ink)] px-3.5 py-2.5 text-[12px] font-bold text-[var(--onSolid)] shadow-[0_10px_28px_-14px_rgba(0,0,0,.45)]",
          className
        )}
      >
        Assistant
        <span
          aria-hidden="true"
          data-tcs-assistant-launcher-count="true"
          className="rounded-full bg-white/15 px-2 py-0.5 text-[11px] font-bold"
        >
          {notes.notes.length}
        </span>
      </button>
    );
  }

  return (
    <>
      {modal ? (
        <div
          aria-hidden="true"
          data-tcs-assistant-scrim="true"
          onClick={closeAssistant}
          className="tcsAssistantModalScrim pointer-events-auto fixed inset-0 cursor-default bg-[rgba(35,33,30,.38)] backdrop-blur-[2px]"
        />
      ) : null}
      <aside
        ref={rootRef}
        data-tcs-assistant-root="true"
        role={modal ? "dialog" : undefined}
        aria-modal={modal ? "true" : undefined}
        tabIndex={modal ? -1 : undefined}
        onKeyDown={handleAssistantKeyDown}
        className={cx(
          // SE-05: `pointer-events-auto` guards against inheriting `pointer-events:
          // none` from a reserved-space ancestor wrapper (see OnboardingShell's
          // assistant column). Compact mode is an explicit modal sheet, so the
          // overlay may cover content only with a scrim, focus trap, and intentional
          // dismissal path. The desktop-expanded root stays in the reserved shell
          // column and keeps the bounded safe-area layout.
          "pointer-events-auto flex min-h-0 flex-col overflow-hidden bg-[var(--rail)]",
          modal || compact
            ? "tcsAssistantDockCompact fixed bottom-3 right-3 top-3 z-40 w-[min(390px,calc(100vw-24px))] rounded-[16px] border border-[var(--line)] shadow-[0_24px_70px_-30px_rgba(0,0,0,.55)]"
            : "tcsAssistantDockExpanded w-[380px] flex-none border-l border-[var(--line)]",
          modal ? "tcsAssistantDockModal" : "",
          className
        )}
        aria-label="Project assistant"
      >
        <header className="tcsAssistantDockHeader shrink-0 border-b border-[var(--line)] px-[18px] pb-[12px] pt-[18px]">
          <div className="tcsAssistantDockMasthead flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <div className="tcsAssistantDockEyebrow text-[10.5px] font-bold uppercase tracking-[.1em] text-[var(--faint)]">
                Assistant
              </div>
              <h2 className="tcsAssistantDockTitle m-0 mt-0.5 truncate text-[17px] font-extrabold tracking-[-.02em] text-[var(--ink)]">
                Ask and capture notes
              </h2>
              <p
                aria-hidden="true"
                className="tcsAssistantDockProject m-0 mt-1 truncate text-[11.5px] font-semibold text-[var(--muted)]"
              >
                {projectLabel}
              </p>
            </div>
            <GroundingBadge groundingState={copilot.groundingState} />
            {collapsible ? (
              <button
                ref={closeButtonRef}
                type="button"
                onClick={closeAssistant}
                aria-expanded="true"
                title="Hide assistant"
                className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[8px] text-[13px] font-extrabold text-[var(--muted2)] hover:bg-[var(--chip)]"
              >
                Hide
              </button>
            ) : null}
          </div>
          <div
            role="tablist"
            aria-label="Assistant sections"
            className="tcsAssistantTabList mt-4 grid grid-cols-2 gap-1 rounded-[10px] bg-[var(--chip)] p-1"
          >
            <TabButton
              active={tab === "ask"}
              label="Ask"
              onClick={() => onTab("ask")}
            />
            <TabButton
              active={tab === "notes"}
              label={`Notes ${notes.notes.length}`}
              onClick={() => onTab("notes")}
            />
          </div>
        </header>

        {tab === "ask" ? (
          <AskTab
            draft={activeDraft.askDraft}
            onDraftChange={(draft) => updateActiveDraft({ askDraft: draft })}
            messages={copilot.messages}
            {...(copilot.turn ? { turn: copilot.turn } : {})}
            {...(projectContextId ? { projectContextId } : {})}
            groundingState={copilot.groundingState}
            isBusy={copilot.isBusy ?? false}
            canAsk={copilot.canAsk ?? true}
            unavailableMessage={copilot.unavailableMessage}
            suggestedPrompts={copilot.suggestedPrompts ?? DEFAULT_PROMPTS}
            inputPlaceholder={
              copilot.inputPlaceholder ??
              "Ask a question or request a change..."
            }
            onSubmit={(text) => {
              const chatCreatedNote = buildChatCreatedNoteInput({
                text,
                messages: copilot.messages,
                projectLabel,
                subjectOptions: notes.subjectOptions
              });

              if (chatCreatedNote) {
                return Promise.resolve(notes.onAddNote(chatCreatedNote)).then(() => {
                  onTab("notes");
                });
              }

              if (isLikelyProjectChangeInstruction(text)) {
                return copilot.onProposedAction(
                  buildDraftAction({
                    id: nextActionId(),
                    instructionText: text
                  })
                );
              }

              return copilot.onAsk(text);
            }}
            onProposedAction={copilot.onProposedAction}
          />
        ) : (
          <AssistantNotesSurface
            notes={notes}
            draft={activeDraft.noteDraft}
            draftKind={activeDraft.noteDraftKind}
            subjectId={activeDraft.noteDraftSubjectId}
            onDraftChange={(draft) => updateActiveDraft({ noteDraft: draft })}
            onDraftKindChange={(kind) => updateActiveDraft({ noteDraftKind: kind })}
            onSubjectIdChange={(subjectId) =>
              updateActiveDraft({ noteDraftSubjectId: subjectId })
            }
          />
        )}
      </aside>
    </>
  );
}

function TabButton({
  active,
  label,
  onClick
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cx(
        "tcsAssistantTabButton rounded-[8px] px-3 py-2 text-[12px] font-extrabold transition-colors",
        active
          ? "bg-[var(--surface)] text-[var(--ink)] shadow-[0_1px_0_rgba(0,0,0,.04)]"
          : "text-[var(--muted)] hover:text-[var(--ink)]"
      )}
    >
      {label}
    </button>
  );
}

function AssistantNotesLoadingState() {
  return (
    <section
      role="tabpanel"
      data-testid="assistant-notes-loading"
      aria-busy="true"
      className="tcsAssistantTabPanel flex min-h-0 flex-1 flex-col gap-3 overflow-hidden p-3"
    >
      <div className="rounded-[11px] border border-dashed border-[var(--line3)] bg-[var(--surface)] px-3 py-4 text-[12.5px] font-semibold leading-[1.5] text-[var(--muted)]">
        Loading project notes…
      </div>
    </section>
  );
}

function AssistantTraceLoadingState() {
  return (
    <section
      data-testid="assistant-trace-loading"
      aria-busy="true"
      role="status"
      className="grid gap-2 rounded-[12px] border border-[var(--line)] bg-[var(--surface)] px-3 py-3 text-[11.5px] font-semibold leading-[1.45] text-[var(--muted)]"
    >
      <div className="flex items-center gap-2 text-[var(--ink2)]">
        <span
          aria-hidden="true"
          className="h-2 w-2 rounded-full bg-[var(--ink)]"
        />
        <span>Loading assistant investigation trace…</span>
      </div>
      <div aria-hidden="true" className="grid gap-1.5">
        <span className="tcsDashboardSkeletonShimmer block h-2.5 w-[78%] rounded-full" />
        <span className="tcsDashboardSkeletonShimmer block h-2.5 w-[58%] rounded-full" />
      </div>
      <div
        role="progressbar"
        aria-label="Loading assistant investigation trace"
        className="h-1 overflow-hidden rounded-full bg-[var(--line2)]"
      >
        <span className="block h-full w-1/3 rounded-full bg-[var(--ink)] animate-tcsLoading" />
      </div>
    </section>
  );
}

function AssistantThinkingState() {
  return (
    <section
      data-testid="assistant-thinking-state"
      aria-busy="true"
      role="status"
      aria-live="polite"
      className="grid gap-2 rounded-[12px] border border-[var(--line)] bg-[var(--surface)] px-3 py-3 text-[11.5px] font-semibold leading-[1.45] text-[var(--muted)]"
    >
      <div className="flex items-center gap-2 text-[var(--ink2)]">
        <span
          aria-hidden="true"
          className="relative flex h-2 w-2 shrink-0"
        >
          <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--faint)] opacity-60" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--ink)]" />
        </span>
        <span>Assistant is checking the project…</span>
      </div>
      <div aria-hidden="true" className="grid gap-1.5">
        <span className="tcsDashboardSkeletonShimmer block h-2.5 w-[86%] rounded-full" />
        <span className="tcsDashboardSkeletonShimmer block h-2.5 w-[64%] rounded-full" />
        <span className="tcsDashboardSkeletonShimmer block h-2.5 w-[44%] rounded-full" />
      </div>
      <div
        role="progressbar"
        aria-label="Assistant response loading"
        className="h-1 overflow-hidden rounded-full bg-[var(--line2)]"
      >
        <span className="block h-full w-1/3 rounded-full bg-[var(--ink)] animate-tcsLoading" />
      </div>
    </section>
  );
}

export function AssistantNotesSurface({
  forceLazy = false,
  ...props
}: AssistantNotesTabProps & { forceLazy?: boolean }) {
  const useEagerTestSurface = import.meta.env.MODE === "test" && !forceLazy;

  if (useEagerTestSurface) {
    return <TestAssistantNotesTab {...props} />;
  }

  return (
    <RetryableLazyRoute
      load={loadAssistantNotesTab}
      surfaceName="Assistant notes"
      fallback={<AssistantNotesLoadingState />}
      render={(AssistantNotesTab) => <AssistantNotesTab {...props} />}
    />
  );
}

function GroundingBadge({
  groundingState
}: {
  groundingState: AssistantGroundingState;
}) {
  const badge =
    groundingState.status === "ready"
      ? {
          label: "Project facts loaded",
          className: "bg-[#EAF1E6] text-[#4E7644]"
        }
      : groundingState.status === "offline"
        ? {
            label: "Unavailable",
            className: "bg-[#F6EEDD] text-[#8A6A2E]"
          }
        : groundingState.status === "checking"
          ? {
              label: "Checking",
              className: "bg-[var(--chip)] text-[var(--muted)]"
            }
          : {
              label: "Needs project facts",
              className: "bg-[#E2ECF4] text-[#3E6E9A]"
            };

  return (
    <span
      className={cx(
        "tcsAssistantGroundingBadge mt-0.5 shrink-0 rounded-full px-2.5 py-1 text-[10.5px] font-bold",
        badge.className
      )}
    >
      {badge.label}
    </span>
  );
}

function AssistantGroundingDetails({
  groundingState
}: {
  groundingState: AssistantGroundingState;
}) {
  return (
    <details
      className="rounded-[10px] border border-[var(--line3)] bg-[var(--surface)] px-3 py-2 text-[11.5px] text-[var(--muted)]"
      data-testid="assistant-grounding-details"
    >
      <summary className="cursor-pointer font-extrabold text-[var(--ink2)]">
        Details
      </summary>
      <div className="mt-2 grid gap-1.5 leading-[1.45]">
        <p className="m-0">
          Answers use this project&apos;s facts, documents, notes, and cited sources.
        </p>
        <p className="m-0">
          Project changes are held as drafts for your review.
        </p>
        <AssistantTechnicalDetailsPanel
          details={groundingState.technicalDetails}
          testId="assistant-grounding-technical-details"
        />
      </div>
    </details>
  );
}

function GroundingPanel({
  groundingState
}: {
  groundingState: AssistantGroundingState;
}) {
  if (groundingState.status === "offline") {
    return (
      <>
        <div className="rounded-[11px] border border-[var(--warn-border)] bg-[var(--warn-bg)] px-3 py-2 text-[12px] font-semibold leading-[1.45] text-[var(--warn)]">
          The assistant is unavailable right now. Notes still save here.
        </div>
        <AssistantGroundingDetails groundingState={groundingState} />
      </>
    );
  }

  if (groundingState.status === "empty") {
    return (
      <>
        <div className="rounded-[11px] border border-[var(--info-border)] bg-[var(--info-bg)] px-3 py-2 text-[12px] font-semibold leading-[1.45] text-[var(--info)]">
          Add project details, documents, or notes first.
        </div>
        <AssistantGroundingDetails groundingState={groundingState} />
      </>
    );
  }

  if (groundingState.status === "checking") {
    return (
      <>
        <div className="rounded-[11px] border border-[var(--line)] bg-[var(--surface)] px-3 py-2 text-[12px] font-semibold leading-[1.45] text-[var(--muted)]">
          The assistant is checking the project.
        </div>
        <AssistantGroundingDetails groundingState={groundingState} />
      </>
    );
  }

  return (
    <>
      <div className="rounded-[11px] border border-[var(--success-border)] bg-[var(--success-bg)] px-3 py-2 text-[12px] font-semibold leading-[1.45] text-[var(--success)]">
        Project information is ready for questions.
      </div>
      <AssistantGroundingDetails groundingState={groundingState} />
    </>
  );
}

function AssistantTabPanel({
  tab,
  children,
  footer
}: {
  tab: AssistantDockTab;
  children: ReactNode;
  footer: ReactNode;
}) {
  return (
    <section
      role="tabpanel"
      data-active-assistant-tab={tab}
      data-testid="assistant-tab-panel"
      className="tcsAssistantTabPanel flex min-h-0 flex-1 flex-col gap-3 overflow-hidden p-3"
    >
      <div
        data-testid="assistant-tab-scroller"
        className="tcsAssistantTabScroller flex min-h-0 flex-1 flex-col gap-3 overflow-auto pr-1"
      >
        {children}
      </div>
      <div data-testid="assistant-tab-footer" className="tcsAssistantTabFooter shrink-0">
        {footer}
      </div>
    </section>
  );
}

function AskTab({
  draft,
  onDraftChange,
  messages,
  groundingState,
  isBusy,
  canAsk: canAskFromController,
  unavailableMessage,
  suggestedPrompts,
  inputPlaceholder,
  onSubmit,
  onProposedAction,
  turn,
  projectContextId
}: {
  draft: string;
  onDraftChange: (draft: string) => void;
  messages: readonly AssistantMessage[];
  groundingState: AssistantGroundingState;
  isBusy: boolean;
  canAsk: boolean;
  unavailableMessage?: string | undefined;
  suggestedPrompts: readonly string[];
  inputPlaceholder: string;
  onSubmit: (text: string) => void | Promise<void>;
  onProposedAction: (action: AssistantProposedAction) => void | Promise<void>;
  turn?: AssistantTurnState;
  projectContextId?: string;
}) {
  const canSend = draft.trim().length > 0 && !isBusy && canAskFromController;
  const traceAnchorIndex = turn ? lastUserMessageIndex(messages) : -1;
  const turnTrace = turn ? (
    <RetryableLazyRoute
      key="assistant-turn-trace"
      load={loadAssistantTurnTrace}
      surfaceName="Assistant investigation trace"
      fallback={<AssistantTraceLoadingState />}
      render={(AssistantTurnTrace) => (
        <AssistantTurnTrace
          turn={turn}
          {...(projectContextId ? { projectId: projectContextId } : {})}
          suppressAnswer={turnAnswerIsAlreadyRendered(turn, messages)}
        />
      )}
    />
  ) : null;
  const conversationNodes: ReactNode[] = [];

  if (messages.length === 0 && !turn) {
    conversationNodes.push(
      <div
        key="assistant-intro"
        className="rounded-[12px] border border-dashed border-[var(--line3)] bg-[var(--surface)] px-3 py-3 text-[12.5px] font-semibold leading-[1.45] text-[var(--muted)]"
        data-testid="assistant-intro"
      >
        Ask about the scope, missing decisions, totals, or notes.
      </div>
    );
  }

  messages.forEach((message, index) => {
    conversationNodes.push(
      <MessageBubble
        key={message.id}
        message={message}
        onProposedAction={onProposedAction}
      />
    );

    if (turnTrace && index === traceAnchorIndex) {
      conversationNodes.push(turnTrace);
    }
  });

  if (turnTrace && traceAnchorIndex < 0) {
    conversationNodes.push(turnTrace);
  }

  if (isBusy && !turn) {
    conversationNodes.push(<AssistantThinkingState key="assistant-thinking" />);
  }

  const submit = async (text: string) => {
    const next = text.trim();

    if (!next || isBusy) {
      return;
    }

    try {
      await onSubmit(next);
      onDraftChange("");
    } catch {
      // The parent owns user-visible error copy. Keep the draft intact.
    }
  };

  return (
    <AssistantTabPanel
      tab="ask"
      footer={
        <div className="grid gap-2">
          <div
            className="tcsAssistantSuggestionsDock rounded-[11px] border border-[var(--line3)] bg-[var(--raised2)] px-2.5 py-2"
            data-testid="assistant-suggestion-tray"
          >
            <div className="mb-1.5 text-[9.5px] font-extrabold uppercase tracking-[.08em] text-[var(--faint)]">
              Quick questions
            </div>
            <div
              className="tcsAssistantSuggestions flex flex-wrap gap-1.5"
              data-testid="assistant-prompt-chips"
            >
              {suggestedPrompts.map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  onClick={() => void submit(prompt)}
                  disabled={isBusy || !canAskFromController}
                  className="min-h-[40px] rounded-full border border-[var(--line)] bg-[var(--surface)] px-2.5 py-1 text-[11.5px] font-bold text-[var(--ink2)] hover:bg-[var(--chip)] disabled:cursor-not-allowed disabled:opacity-55"
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>

          <div className="tcsAssistantAskActions tcsAssistantActionCard rounded-[12px] border border-[var(--line3)] bg-[var(--surface)] p-2.5">
            <textarea
              aria-label="Message the project assistant"
              value={draft}
              onChange={(event) => onDraftChange(event.currentTarget.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  void submit(draft);
                }
              }}
              rows={2}
              placeholder={inputPlaceholder}
              disabled={!canAskFromController}
              className="tcsAssistantAskTextarea tcsAssistantActionTextarea min-h-[48px] w-full resize-y border-none bg-transparent text-[12.5px] leading-[1.45] text-[var(--ink)] outline-none placeholder:text-[var(--faint)]"
            />
            <div className="tcsAssistantAskComposerActions tcsAssistantActionRow flex items-center gap-2">
              <div className="tcsAssistantAskComposerHint text-[10.5px] font-semibold text-[var(--faint)]">
                {unavailableMessage ?? "Changes become drafts first."}
              </div>
              <button
                type="button"
                onClick={() => void submit(draft)}
                disabled={!canSend}
                className={cx(
                  "tcsAssistantAskSend ml-auto rounded-[9px] px-4 py-2 text-[12px] font-bold",
                  canSend
                    ? "bg-[var(--ink)] text-[var(--onSolid)]"
                    : "bg-[var(--line2)] text-[var(--faint)]"
                )}
              >
                {isBusy ? "Sending..." : "Send"}
              </button>
            </div>
          </div>
        </div>
      }
    >
      <GroundingPanel groundingState={groundingState} />
      <div className="flex min-h-0 flex-col gap-2">{conversationNodes}</div>
    </AssistantTabPanel>
  );
}

function MessageBubble({
  message,
  onProposedAction
}: {
  message: AssistantMessage;
  onProposedAction: (action: AssistantProposedAction) => void | Promise<void>;
}) {
  if (message.role === "user") {
    return (
      <article className="ml-auto max-w-[88%] rounded-[11px] rounded-br-[4px] bg-[var(--ink)] px-3 py-2 text-[12.5px] font-semibold leading-[1.45] text-[var(--onSolid)]">
        {message.text}
      </article>
    );
  }

  if (message.role === "error") {
    return (
      <article className="mr-auto max-w-[92%] rounded-[11px] rounded-bl-[4px] border border-[#E8B7AF] bg-[#F8E8E5] px-3 py-2 text-[12.5px] font-semibold leading-[1.45] text-[#A23F33]">
        {message.text}
      </article>
    );
  }

  if (message.role === "proposed-action" && message.proposedAction) {
    return (
      <article className="mr-auto max-w-[94%] rounded-[12px] border border-[#D7E1EA] bg-[#F6FAFD] px-3 py-3 text-[12.5px] text-[var(--ink2)]">
        <div className="text-[10.5px] font-bold uppercase tracking-[.1em] text-[#3E6E9A]">
          Reviewable draft
        </div>
        <h3 className="m-0 mt-1 text-[14px] font-extrabold text-[var(--ink)]">
          {message.proposedAction.title}
        </h3>
        <p className="m-0 mt-1 leading-[1.45]">{message.text}</p>
        <button
          type="button"
          onClick={() => runCallback(onProposedAction(message.proposedAction!))}
          className="mt-3 rounded-[9px] bg-[var(--ink)] px-3 py-2 text-[11.5px] font-bold text-[var(--onSolid)]"
        >
          Review draft
        </button>
      </article>
    );
  }

  const citations = message.citations ?? [];
  const technicalDetails = messageTechnicalDetails(message);
  const hasAnswerEvidence =
    citations.length > 0 || hasAssistantTechnicalDetails(technicalDetails);

  return (
    <article
      className={cx(
        "mr-auto max-w-[94%] rounded-[11px] rounded-bl-[4px] border px-3 py-2 text-[12.5px] font-semibold leading-[1.5]",
        message.role === "applied"
          ? "border-[#CFE2C6] bg-[#EAF1E6] text-[#3F6235]"
          : message.role === "status"
            ? "border-[#D7E1EA] bg-[#F6FAFD] text-[#3E6E9A]"
            : "border-[var(--line)] bg-[var(--surface)] text-[var(--ink2)]"
      )}
    >
      {message.grounding === "ungrounded" ? (
        <details
          data-testid="assistant-message-ungrounded"
          className="mb-2 rounded-[8px] border border-[#E9D3AD] bg-[#FFF8E9] px-2.5 py-1.5 text-[10.5px] leading-[1.4] text-[#8A6A2E]"
        >
          <summary className="cursor-pointer font-extrabold">
            Details
          </summary>
          <p className="m-0 mt-1 font-semibold">
            No retrieval trace was recorded behind this response.
          </p>
        </details>
      ) : null}
      <MarkdownMessage
        text={message.text}
        collapseEvidence
        evidence={
          hasAnswerEvidence ? (
            <>
              {citations.length > 0 ? <CitationList citations={citations} /> : null}
              <AssistantTechnicalDetailsPanel
                details={technicalDetails}
                testId="assistant-message-technical-details"
              />
            </>
          ) : undefined
        }
      />
    </article>
  );
}

function lastUserMessageIndex(messages: readonly AssistantMessage[]): number {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index]?.role === "user") {
      return index;
    }
  }

  return -1;
}

function turnAnswerIsAlreadyRendered(
  turn: AssistantTurnState,
  messages: readonly AssistantMessage[]
): boolean {
  const finalAnswer = [...turn.events]
    .reverse()
    .find(
      (event): event is Extract<AssistantTurnEvent, { type: "final_answer" }> =>
        event.type === "final_answer"
    );

  return Boolean(
    finalAnswer &&
    messages.some(
      (message) => message.role === "assistant" && message.text === finalAnswer.answer
    )
  );
}

function messageTechnicalDetails(
  message: AssistantMessage
): AssistantTechnicalDetails | undefined {
  if (message.technicalDetails) {
    return message.technicalDetails;
  }

  if (!message.providerLabel) {
    return undefined;
  }

  return {
    sources: [
      {
        label: "Assistant response",
        detail: "Response metadata is available for support review."
      }
    ],
    checks: [
      {
        label: "Answer metadata",
        detail: message.providerLabel
      }
    ],
    rawData: {
      messageId: message.id,
      role: message.role,
      providerLabel: message.providerLabel
    }
  };
}

function AssistantTechnicalDetailsPanel({
  details,
  testId
}: {
  details?: AssistantTechnicalDetails | undefined;
  testId?: string | undefined;
}) {
  if (!hasAssistantTechnicalDetails(details)) {
    return null;
  }

  return (
    <TechnicalDetails
      className="mt-2"
      {...(details.sources ? { sources: details.sources } : {})}
      {...(details.checks ? { checks: details.checks } : {})}
      {...(details.rawData !== undefined ? { rawData: details.rawData } : {})}
      {...(testId ? { "data-testid": testId } : {})}
    />
  );
}

function hasAssistantTechnicalDetails(
  details: AssistantTechnicalDetails | undefined
): details is AssistantTechnicalDetails {
  return Boolean(
    details &&
    ((details.sources?.length ?? 0) > 0 ||
      (details.checks?.length ?? 0) > 0 ||
      details.rawData !== undefined)
  );
}

function CitationList({ citations }: { citations: readonly AssistantCitation[] }) {
  const grouped = useMemo(
    () => ({
      source: citations.filter((citation) => citation.kind === "source"),
      rule: citations.filter((citation) => citation.kind === "rule"),
      note: citations.filter((citation) => citation.kind === "note"),
      fact: citations.filter((citation) => citation.kind === "fact")
    }),
    [citations]
  );
  const visibleGroups = [
    { label: "Sources", items: grouped.source },
    { label: "Rules", items: grouped.rule },
    { label: "Notes", items: grouped.note },
    { label: "Facts", items: grouped.fact }
  ].filter((group) => group.items.length > 0);

  return (
    <div className="mt-3 grid gap-2">
      {visibleGroups.map((group) => (
        <div key={group.label} className="rounded-[9px] bg-[var(--raised)] px-2.5 py-2">
          <div className="text-[10px] font-bold uppercase tracking-[.08em] text-[var(--faint)]">
            {group.label}
          </div>
          <ul className="m-0 mt-1 grid list-none gap-1 p-0">
            {group.items.slice(0, 5).map((citation) => (
              <li key={citation.id} className="text-[11.5px] text-[var(--muted)]">
                {citation.href ? (
                  <a
                    href={citation.href}
                    title={citation.targetLabel}
                    className="font-extrabold text-[#3E6E9A] underline decoration-[#A9C4D8] underline-offset-2 hover:text-[var(--ink)]"
                  >
                    {citation.label}
                  </a>
                ) : (
                  <span className="font-extrabold text-[var(--ink2)]">
                    {citation.label}
                  </span>
                )}
                {citation.locator ? ` - ${citation.locator}` : ""}
                {citation.unavailableReason ? (
                  <span className="block text-[10px] text-[var(--faint)]">
                    Reference only · {citation.unavailableReason}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
